import { Typography } from "@mui/material"
import Grid from "@mui/material/Grid"
import { Context, ReactElement, useContext } from "react"
import { useForm } from "react-hook-form"

import { TypeTestRegion } from "../../../../../constants/enums"
import { typeTestStandards } from "../../../../../constants/ttr/registerDevice"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import {
    getFullyTestedRegisterName,
    getPartiallyTestedRegisterName,
    getTypeTestLabelNameFromRegisterName,
} from "../../../../../utils/registerDevice/registerDeviceUtils"
import FormErrors from "../../../../formErrors/formErrors"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import { IDeviceFormContextValue } from "../../../types/providerTypes"
import TypeTest from "../../components/typeTest/typeTest"
import { TTRDeviceFormData, TypeTestFormData } from "../../types/providerTypes"

const mapRegisterNameToValues = (
    formData: TTRDeviceFormData
): { [name: string]: NonNullable<unknown> | undefined } => {
    const mapping: { [name: string]: NonNullable<unknown> | undefined } = {}

    formData.typeTestDetails.forEach((data) => {
        const fullyTestedRegisterName = getFullyTestedRegisterName(data.name)
        mapping[fullyTestedRegisterName] = data.fullyTested

        const partiallyTestedRegisterName = getPartiallyTestedRegisterName(
            data.name
        )
        mapping[partiallyTestedRegisterName] = data.partiallyTested
    })

    return mapping
}

interface ITypeTestDetailsProps {
    context: Context<IDeviceFormContextValue<TTRDeviceFormData>>
}

/** Displays groups of British and Northern Irish standards. The user can toggle
 * fully tested and partially tested for each standard
 */
const TypeTestDetails = ({ context }: ITypeTestDetailsProps): ReactElement => {
    const {
        formData,
        setFormData,
        currentIndex,
        setCurrentIndex,
        saveDraft,
        saveDraftLoading,
    } = useContext(context)
    const {
        handleSubmit,
        control,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: mapRegisterNameToValues(formData),
        shouldUnregister: true,
    })

    const getUpdatedFormData = (): TTRDeviceFormData => {
        const updatedData: TypeTestFormData[] = formData.typeTestDetails.map(
            (data) => ({
                name: data.name,
                displayName: data.displayName,
                fullyTested: watch(
                    getFullyTestedRegisterName(data.name)
                ) as boolean,
                partiallyTested: watch(
                    getPartiallyTestedRegisterName(data.name)
                ) as boolean,
            })
        )

        const updatedFormData: TTRDeviceFormData = {
            ...formData,
            typeTestDetails: updatedData,
        }

        return updatedFormData
    }

    const updateTypeTestFormData = (): void => {
        const updatedFormData = getUpdatedFormData()
        setFormData(updatedFormData)
    }

    const onClickNext = (): void => {
        updateTypeTestFormData()
        setCurrentIndex(currentIndex + 1)
    }

    const onClickBack = (): void => {
        updateTypeTestFormData()
        setCurrentIndex(currentIndex - 1)
    }

    const onSaveDraft = (): void => {
        const updatedFormData = getUpdatedFormData()
        setFormData(updatedFormData)
        saveDraft(updatedFormData)
    }

    const northernIrishStandards = typeTestStandards.filter(
        (standard) => standard.region === TypeTestRegion.northernIreland
    )

    const britishStandards = typeTestStandards.filter(
        (standard) => standard.region === TypeTestRegion.greatBritain
    )

    return (
        <Grid container spacing={GRID_SPACING} marginTop={0}>
            <Grid item marginBottom={GRID_SPACING}>
                <FormHeader
                    header="Type Test Details"
                    subHeader="This section of the form allows you to define the type test
                    status of the device"
                    index={currentIndex + 1}
                    headerTypography={{
                        color: "text.primary",
                    }}
                />
            </Grid>

            <Grid
                item
                container
                spacing={GRID_SPACING}
                marginBottom={GRID_SPACING * 2}
            >
                <Grid item xs={12}>
                    <Typography color="text.secondary" fontSize="1.25rem">
                        {TypeTestRegion.greatBritain}
                    </Typography>
                </Grid>
                {britishStandards.map((details) => (
                    <Grid item xs={12} sm={12} md={4} key={details.name}>
                        <TypeTest
                            control={control}
                            watch={watch}
                            details={details}
                            name={details.name}
                        />
                    </Grid>
                ))}
            </Grid>

            <Grid item container spacing={GRID_SPACING}>
                <Grid item xs={12}>
                    <Typography color="text.secondary" fontSize="1.25rem">
                        {TypeTestRegion.northernIreland}
                    </Typography>
                </Grid>
                {northernIrishStandards.map((details) => (
                    <Grid item xs={12} sm={12} md={4} key={details.name}>
                        <TypeTest
                            control={control}
                            watch={watch}
                            details={details}
                            name={details.name}
                        />
                    </Grid>
                ))}
            </Grid>

            <Grid item container spacing={GRID_SPACING}>
                <Grid item xs={12}>
                    <FormErrors
                        errors={errors}
                        getFriendlyName={(key: string) =>
                            getTypeTestLabelNameFromRegisterName(key)
                        }
                    />
                </Grid>
            </Grid>

            <Grid item container spacing={GRID_SPACING}>
                <Grid item xs={12}>
                    <FormNavigation
                        backText="Back"
                        nextText="Next"
                        onClickBack={onClickBack}
                        onClickNext={() => handleSubmit(onClickNext)()}
                        onClickMiddle={onSaveDraft}
                        middleDisabled={saveDraftLoading}
                    />
                </Grid>
            </Grid>
        </Grid>
    )
}

export default TypeTestDetails
